/**
 * ParseErrorFallback — diagnostic panel for VQE-RO when the current statement
 * cannot be cleanly parsed. Two surfaces: "no-statement" (cursor not inside
 * any statement) and "with-error" (`RecoveryInfo.errorRanges` overlaps the
 * statement). PRD: `Ralph/VisualQueryEditorReadOnly/PRD.md` § "Fallback —
 * Parse-error Visualization".
 */

import { Button } from '../../../../../components/ui/button';
import type {
  RecoveryInfo,
  SqlRange,
  StatementInfo,
} from '../../../../sql-grammar/model';
import { GrammarHarnessReportButton } from '../../harness/GrammarHarnessReportButton';
import type { ReportGrammarIssueDeps } from '../../harness/reportGrammarIssue';
import { vqeCopy } from '../../ux/vqeCopy';
import { StatementTextDisclosure } from './StatementTextDisclosure';
import { FULL_CANVAS_ROOT_CLASS } from '../common/canvasLayout';
import { STATEMENT_KIND_LABELS } from './statementKindLabels';

export type ParseErrorFallbackProps = {
  statement: StatementInfo | null;
  recovery: RecoveryInfo;
  bufferText: string;
  onEditAsSql: (errorOffset: number | null) => void;
  dialect?: string;
  reportApi?: ReportGrammarIssueDeps;
};

function rangesOverlap(a: SqlRange, b: SqlRange): boolean {
  if (a.endOffset < b.startOffset) return false;
  if (a.startOffset > b.endOffset) return false;
  return true;
}

function pickFirstErrorRange(
  statement: StatementInfo | null,
  recovery: RecoveryInfo,
): SqlRange | null {
  if (recovery.errorRanges.length === 0) return null;
  if (!statement) return recovery.errorRanges[0] ?? null;
  for (const range of recovery.errorRanges) {
    if (rangesOverlap(range, statement.range)) return range;
  }
  return null;
}

function snippetAt(bufferText: string, range: SqlRange): string {
  const start = Math.max(0, range.startOffset);
  const end = Math.max(start, Math.min(bufferText.length, range.endOffset));
  const raw = bufferText.slice(start, end);
  // Multi-line spans collapse to the first line; long spans truncate with an
  // ellipsis so the headline stays compact.
  const newlineIndex = raw.indexOf('\n');
  const oneLine = newlineIndex === -1 ? raw : raw.slice(0, newlineIndex);
  const trimmed = oneLine.trim();
  if (trimmed.length === 0) return '';
  if (trimmed.length > 32 || newlineIndex !== -1) {
    const head = trimmed.slice(0, Math.min(32, trimmed.length));
    return `${head}…`;
  }
  return trimmed;
}

function describeErrorRange(bufferText: string, range: SqlRange): string {
  const line = range.startLine + 1;
  const column = range.startColumn + 1;
  const snippet = snippetAt(bufferText, range);
  if (snippet.length === 0) {
    return `Unexpected token at line ${line} column ${column}.`;
  }
  return `Unexpected token \`${snippet}\` at line ${line} column ${column}.`;
}

export function ParseErrorFallback({
  statement,
  recovery,
  bufferText,
  onEditAsSql,
  dialect,
  reportApi,
}: ParseErrorFallbackProps) {
  const errorRange = pickFirstErrorRange(statement, recovery);
  const isNoStatement = statement === null;
  const kindLabel = statement ? (STATEMENT_KIND_LABELS[statement.kind] ?? statement.kind) : null;
  const body = statement
    ? bufferText.slice(
        Math.max(0, statement.range.startOffset),
        Math.max(statement.range.startOffset, Math.min(bufferText.length, statement.range.endOffset)),
      )
    : '';

  const title = isNoStatement ? 'No statement at cursor' : 'Parse error — read-only';
  const hint = isNoStatement
    ? 'No SQL statement was parsed at the current cursor. The buffer text is preserved — Edit as SQL to keep typing.'
    : 'The parser couldn’t finish this statement. Tokenization may have recovered mid-clause; the buffer text is preserved.';

  const errorOffset = errorRange?.startOffset ?? null;
  const surface: 'no-statement' | 'with-error' = isNoStatement ? 'no-statement' : 'with-error';

  return (
    <div
      className={FULL_CANVAS_ROOT_CLASS}
      data-testid="vqe-parse-error-fallback"
      data-surface={surface}
    >
      {/* One card: title + hint, the statement facts (with-error surface), and
          the actions — the same shape as ReadOnlyFallback. The workspace hides
          its read-only banner while this panel is shown. */}
      <div className="rounded-lg border border-border bg-card p-4 shadow-sm" data-testid="vqe-parse-error-card">
        <h3 className="text-sm font-semibold text-foreground" data-testid="vqe-parse-error-fallback-title">
          {title}
        </h3>
        <p className="mt-1 text-sm text-muted-foreground" data-testid="vqe-parse-error-fallback-hint">
          {hint}
        </p>

        {!isNoStatement && (
          <>
            <div className="mt-3 flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <span className="text-xs uppercase tracking-wide text-muted-foreground">Statement</span>
              <span
                className="font-mono text-sm font-medium text-foreground"
                data-testid="vqe-parse-error-fallback-kind"
              >
                {kindLabel}
              </span>
            </div>

            {errorRange ? (
              <p
                className="mt-3 font-mono text-xs text-destructive"
                data-testid="vqe-parse-error-fallback-error-message"
              >
                {describeErrorRange(bufferText, errorRange)}
              </p>
            ) : (
              <p
                className="mt-3 text-xs text-muted-foreground"
                data-testid="vqe-parse-error-fallback-error-message"
              >
                The parser flagged this statement but did not localize a specific token.
              </p>
            )}

            <StatementTextDisclosure testIdPrefix="vqe-parse-error-fallback">{body}</StatementTextDisclosure>
          </>
        )}

        <div className="mt-4 flex flex-wrap items-center gap-2">
          {dialect ? (
            <GrammarHarnessReportButton
              reason="parse-error"
              bufferText={bufferText}
              dialect={dialect}
              statementId={statement?.id ?? null}
              diagnostic={
                errorRange
                  ? `ParseErrorFallback: ${describeErrorRange(bufferText, errorRange)}`
                  : `ParseErrorFallback: ${isNoStatement ? 'no-statement-at-cursor' : 'no-localized-error-token'}`
              }
              api={reportApi}
            />
          ) : null}
          <Button
            variant="default"
            size="sm"
            onClick={() => onEditAsSql(errorOffset)}
            data-testid="vqe-parse-error-fallback-edit-as-sql"
          >
            {vqeCopy.actions.editAsSql}
          </Button>
        </div>
      </div>
    </div>
  );
}
